import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import InvestmentTabbedForm from '../components/investments/InvestmentTabbedForm';
import {
    buildInvestmentUpdatePayload,
    createEmptyInvestmentFormState,
    investmentToFormState
} from '../components/investments/investmentFormUtils';
import { getApiUrl } from '../config/api.config';
import { getDashboardPath } from '../lib/navigation';

const createAdminInvestmentFormState = () => ({
    ...createEmptyInvestmentFormState(),
    investorId: '',
    startupId: '',
    linkToInvestorNetworkId: ''
});

const InvestmentManagement = () => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [investments, setInvestments] = useState([]);
    const [investors, setInvestors] = useState([]);
    const [investorNetworks, setInvestorNetworks] = useState([]);
    const [startups, setStartups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const dashboardPath = getDashboardPath(tenantId, user.role);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingInvestment, setEditingInvestment] = useState(null);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
    const [newInvestment, setNewInvestment] = useState(createAdminInvestmentFormState);

    const investmentCounterparties = useMemo(() => {
        const inv = (investors || []).map((u) => ({
            id: u.id,
            displayName: `${u.name || '—'} (${u.email || u.id}) · Investor`
        }));
        const nets = (investorNetworks || []).map((u) => ({
            id: u.id,
            displayName: `${u.organizationName || u.name || '—'} (${u.email || u.id}) · Network`
        }));
        return [...inv, ...nets];
    }, [investors, investorNetworks]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            const [investmentsRes, investorsRes, networksRes, startupsRes] = await Promise.all([
                fetch(getApiUrl('investments'), { headers }),
                fetch(getApiUrl('admin/users/investors'), { headers }),
                fetch(getApiUrl('admin/users/investor-networks'), { headers }),
                fetch(getApiUrl('admin/users/startups'), { headers })
            ]);

            if (investmentsRes.ok) {
                const investmentsData = await investmentsRes.json();
                setInvestments(Array.isArray(investmentsData) ? investmentsData : []);
            } else {
                setInvestments([]);
            }

            if (investorsRes.ok) {
                const investorsData = await investorsRes.json();
                setInvestors(Array.isArray(investorsData) ? investorsData : []);
            } else {
                setInvestors([]);
            }

            if (networksRes.ok) {
                const networksData = await networksRes.json();
                setInvestorNetworks(Array.isArray(networksData) ? networksData : []);
            } else {
                setInvestorNetworks([]);
            }

            if (startupsRes.ok) {
                const startupsData = await startupsRes.json();
                setStartups(Array.isArray(startupsData) ? startupsData : []);
            } else {
                setStartups([]);
            }
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetNewInvestmentForm = () => createAdminInvestmentFormState();

    const handleCreateInvestment = async () => {
        try {
            const token = localStorage.getItem('token');
            const invAmt = parseFloat(newInvestment.investmentAmount);
            if (!newInvestment.investorId || !newInvestment.startupId || isNaN(invAmt)) {
                setAlert({ isOpen: true, title: 'Validation', message: 'Select counterparty (investor or network), startup, and a valid amount.', type: 'error' });
                return;
            }
            const payload = {
                investorId: Number(newInvestment.investorId),
                startupId: Number(newInvestment.startupId),
                investmentDate: newInvestment.investmentDate ? `${newInvestment.investmentDate}T00:00:00` : null,
                investmentAmount: invAmt,
                currency: newInvestment.currency || 'INR',
                equityPercentage: newInvestment.equityPercentage ? parseFloat(newInvestment.equityPercentage) : null,
                entrySharePrice: newInvestment.entrySharePrice ? parseFloat(newInvestment.entrySharePrice) : null,
                entryShareEquity: newInvestment.entryShareEquity ? parseFloat(newInvestment.entryShareEquity) : null,
                numberOfShares: newInvestment.numberOfShares ? parseInt(newInvestment.numberOfShares, 10) : null,
                investmentStage: newInvestment.investmentStage,
                investmentType: newInvestment.investmentType,
                accessLevel: newInvestment.accessLevel,
                autoGrantAccess: newInvestment.autoGrantAccess,
                boardSeat: newInvestment.boardSeat,
                leadInvestor: newInvestment.leadInvestor,
                notes: newInvestment.notes || null,
                linkToInvestorNetworkId: newInvestment.linkToInvestorNetworkId ? Number(newInvestment.linkToInvestorNetworkId) : null
            };
            const response = await fetch(getApiUrl('investments'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setAlert({
                    isOpen: true,
                    title: 'Success',
                    message: 'Investment created. File access was granted when auto-grant is enabled. Startup is linked to relevant investor network(s).',
                    type: 'success'
                });
                setShowCreateModal(false);
                setNewInvestment(resetNewInvestmentForm());
                loadData();
            } else {
                let msg = 'Failed to create investment';
                try {
                    const errJson = await response.json();
                    msg = errJson.message || errJson.error || JSON.stringify(errJson);
                } catch {
                    msg = await response.text();
                }
                setAlert({ isOpen: true, title: 'Error', message: msg, type: 'error' });
            }
        } catch (error) {
            console.error('Failed to create investment:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to create investment', type: 'error' });
        }
    };

    const handleMarkAsExited = (id) => {
        setConfirm({
            isOpen: true,
            title: 'Mark as Exited',
            message: 'Mark this investment as exited? This will revoke all file access permissions.',
            type: 'warning',
            onConfirm: async () => {
                const exitAmount = prompt('Enter exit amount (optional):');
                const exitValuation = prompt('Enter company valuation at exit (optional):');
                await performMarkAsExited(id, exitAmount, exitValuation);
            }
        });
    };

    const performMarkAsExited = async (id, exitAmount, exitValuation) => {

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl(`investments/${id}/exit`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    exitDate: new Date().toISOString(),
                    exitAmount: exitAmount ? parseFloat(exitAmount) : null,
                    exitValuation: exitValuation ? parseFloat(exitValuation) : null
                })
            });

            if (response.ok) {
                setAlert({ isOpen: true, title: 'Success', message: 'Investment marked as exited. File permissions have been revoked.', type: 'success' });
                loadData();
            } else {
                setAlert({ isOpen: true, title: 'Error', message: 'Failed to mark as exited', type: 'error' });
            }
        } catch (error) {
            console.error('Failed to mark as exited:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to mark as exited', type: 'error' });
        }
    };

    const handleEditInvestment = (investment) => {
        setEditingInvestment(investment);
        setNewInvestment({
            ...createAdminInvestmentFormState(),
            ...investmentToFormState(investment),
            investorId: investment.investor?.id || investment.investorId || '',
            startupId: investment.startup?.id || investment.startupId || '',
            linkToInvestorNetworkId: ''
        });
        setShowEditModal(true);
    };

    const handleUpdateInvestment = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl(`investments/${editingInvestment.id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(buildInvestmentUpdatePayload(newInvestment))
            });

            if (response.ok) {
                setAlert({ isOpen: true, title: 'Success', message: 'Investment updated successfully!', type: 'success' });
                setShowEditModal(false);
                setEditingInvestment(null);
                loadData();
            } else {
                let msg = 'Failed to update investment';
                try {
                    const errJson = await response.json();
                    msg = errJson.message || errJson.error || msg;
                } catch {
                    msg = await response.text();
                }
                setAlert({ isOpen: true, title: 'Error', message: msg, type: 'error' });
            }
        } catch (error) {
            console.error('Failed to update investment:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to update investment', type: 'error' });
        }
    };

    const handleDeleteInvestment = (id) => {
        setConfirm({
            isOpen: true,
            title: 'Delete Investment',
            message: 'Delete this investment? This will revoke all file permissions.',
            type: 'danger',
            onConfirm: async () => {
                await performDeleteInvestment(id);
            }
        });
    };

    const performDeleteInvestment = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl(`investments/${id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setAlert({ isOpen: true, title: 'Success', message: 'Investment deleted successfully', type: 'success' });
                loadData();
            } else {
                setAlert({ isOpen: true, title: 'Error', message: 'Failed to delete investment', type: 'error' });
            }
        } catch (error) {
            console.error('Failed to delete investment:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to delete investment', type: 'error' });
        }
    };

    const formatCurrency = (amount, currency) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency || 'INR'
        }).format(amount);
    };

    const getStatusBadge = (status) => {
        const colors = {
            ACTIVE: 'bg-green-100 text-green-800',
            EXITED: 'bg-gray-100 text-gray-800',
            PENDING: 'bg-yellow-100 text-yellow-800',
            CANCELLED: 'bg-red-100 text-red-800'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
                {status}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <Header />

            {/* Page Header */}
            <div className="bg-white shadow pt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button onClick={() => navigate(dashboardPath)} className="text-gray-600 hover:text-gray-900">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Investment Management</h1>
                                <p className="text-sm text-gray-500 mt-1">Manage investor-startup relationships and automatic file access</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            <span>Create Investment</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm font-medium text-gray-500">Total Investments</div>
                        <div className="mt-2 text-3xl font-bold text-gray-900">{investments.length}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm font-medium text-gray-500">Active Investments</div>
                        <div className="mt-2 text-3xl font-bold text-green-600">
                            {investments.filter(i => i.status === 'ACTIVE').length}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm font-medium text-gray-500">Total Investors</div>
                        <div className="mt-2 text-3xl font-bold text-gray-900">{investors.length}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm font-medium text-gray-500">Total Startups</div>
                        <div className="mt-2 text-3xl font-bold text-gray-900">{startups.length}</div>
                    </div>
                </div>

                {/* Investments Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">All Investments</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Investor</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Startup</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equity %</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CCPS</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Level</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {investments.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                                            No investments yet. Create your first investment to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    investments.map((investment) => (
                                        <tr key={investment.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{investment.investorName}</div>
                                                        <div className="text-sm text-gray-500">{investment.investorEmail}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{investment.startupName}</div>
                                                <div className="text-sm text-gray-500">{investment.startupEmail}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {formatCurrency(investment.investmentAmount, investment.currency)}
                                                </div>
                                                {investment.leadInvestor && (
                                                    <span className="text-xs text-blue-600 font-medium">Lead</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {investment.investmentType ? investment.investmentType.replace(/_/g, ' ') : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {investment.equityPercentage ? `${investment.equityPercentage}%` : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {investment.numberOfShares != null ? investment.numberOfShares : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">{investment.investmentStage}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${investment.accessLevel === 'FULL' ? 'bg-green-100 text-green-800' :
                                                    investment.accessLevel === 'LIMITED' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {investment.accessLevel}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(investment.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                                <button
                                                    onClick={() => handleEditInvestment(investment)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Edit"
                                                >
                                                    Edit
                                                </button>
                                                {investment.status === 'ACTIVE' && (
                                                    <button
                                                        onClick={() => handleMarkAsExited(investment.id)}
                                                        className="text-orange-600 hover:text-orange-900"
                                                        title="Mark as Exited"
                                                    >
                                                        Exit
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteInvestment(investment.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Create Investment Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Create New Investment</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Counterparty can be an investor or an investor network. The startup is linked in <code className="text-xs bg-gray-100 px-1 rounded">investor_network_startups</code> when a network applies; you can add an extra network link below.
                            </p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Investor or network *</label>
                                    <select
                                        value={newInvestment.investorId}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, investorId: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        required
                                    >
                                        <option value="">Select investor or investor network</option>
                                        {investmentCounterparties.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.displayName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Startup *</label>
                                    <select
                                        value={newInvestment.startupId}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, startupId: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        required
                                    >
                                        <option value="">Select Startup (user account)</option>
                                        {startups.map((startup) => (
                                            <option key={startup.id} value={startup.id}>
                                                {startup.organizationName || startup.name} ({startup.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Also link startup to network (optional)</label>
                                    <select
                                        value={newInvestment.linkToInvestorNetworkId}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, linkToInvestorNetworkId: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="">— None (use only automatic links from counterparty) —</option>
                                        {(investorNetworks || []).map((n) => (
                                            <option key={n.id} value={n.id}>
                                                {n.organizationName || n.name} ({n.email})
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">Adds a row in <strong>investor_network_startups</strong> for syndicate visibility.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Investment Date</label>
                                    <input
                                        type="date"
                                        value={newInvestment.investmentDate}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, investmentDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Investment Amount</label>
                                    <input
                                        type="number"
                                        value={newInvestment.investmentAmount}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, investmentAmount: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="10000000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                    <select
                                        value={newInvestment.currency}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, currency: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="INR">INR</option>
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Equity %</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newInvestment.equityPercentage}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, equityPercentage: e.target.value })}
                                        onWheel={(e) => e.target.blur()}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="10.5"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Entry Share Price</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newInvestment.entrySharePrice}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, entrySharePrice: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="100.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Entry Share Equity</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newInvestment.entryShareEquity}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, entryShareEquity: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="10000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">No. of shares (CCPS)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={newInvestment.numberOfShares}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, numberOfShares: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Optional"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Investment Stage</label>
                                    <select
                                        value={newInvestment.investmentStage}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, investmentStage: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="PRE_SEED">Pre-Seed</option>
                                        <option value="SEED">Seed</option>
                                        <option value="SERIES_A">Series A</option>
                                        <option value="SERIES_B">Series B</option>
                                        <option value="SERIES_C">Series C</option>
                                        <option value="GROWTH">Growth</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Investment Type</label>
                                    <select
                                        value={newInvestment.investmentType}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, investmentType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="EQUITY">Equity</option>
                                        <option value="CCPS">CCPS</option>
                                        <option value="CCD">CCD</option>
                                        <option value="CONVERTIBLE_NOTE">Convertible Note</option>
                                        <option value="SAFE">SAFE</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Access Level</label>
                                    <select
                                        value={newInvestment.accessLevel}
                                        onChange={(e) => setNewInvestment({ ...newInvestment, accessLevel: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="FULL">Full (Download)</option>
                                        <option value="LIMITED">Limited (Download)</option>
                                        <option value="VIEW_ONLY">View Only</option>
                                    </select>
                                </div>

                                <div className="col-span-2 flex flex-wrap items-center gap-4">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={newInvestment.leadInvestor}
                                            onChange={(e) => setNewInvestment({ ...newInvestment, leadInvestor: e.target.checked })}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Lead investor</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={newInvestment.boardSeat}
                                            onChange={(e) => setNewInvestment({ ...newInvestment, boardSeat: e.target.checked })}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Board seat</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={newInvestment.autoGrantAccess}
                                            onChange={(e) => setNewInvestment({ ...newInvestment, autoGrantAccess: e.target.checked })}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Auto-grant file access from startup files</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={newInvestment.notes}
                                    onChange={(e) => setNewInvestment({ ...newInvestment, notes: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    rows="3"
                                    placeholder="Additional notes about this investment..."
                                />
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="text-sm text-blue-800 space-y-1">
                                        <p><strong>Data stored:</strong> Row in <code className="text-xs bg-white/80 px-1 rounded">investments</code> (amount, round, type, equity, CCPS, access flags). Optional links in <code className="text-xs bg-white/80 px-1 rounded">investor_network_startups</code>.</p>
                                        <p><strong>Files:</strong> When auto-grant is on, file permissions follow <strong>Access level</strong> (Full/Limited → download, View only → view).</p>
                                        <p className="text-xs">Admins can record investments even if the investor has not paid the activity fee (investors still require fee for self-service flows).</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateInvestment}
                                disabled={!newInvestment.investorId || !newInvestment.startupId}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                Create Investment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Investment Modal */}
            {showEditModal && editingInvestment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Edit Investment</h3>
                            <p className="text-sm text-gray-500 mt-1">Update investment details</p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                                <p className="text-gray-600"><span className="font-medium text-gray-800">Counterparty:</span>{' '}
                                    {editingInvestment?.investorName || '—'} <span className="text-gray-400">({editingInvestment?.investorEmail})</span>
                                </p>
                                <p className="text-gray-600 mt-1"><span className="font-medium text-gray-800">Startup:</span>{' '}
                                    {editingInvestment?.startupName || '—'} <span className="text-gray-400">({editingInvestment?.startupEmail})</span>
                                </p>
                            </div>
                            <InvestmentTabbedForm value={newInvestment} onChange={setNewInvestment} />
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    setEditingInvestment(null);
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateInvestment}
                                disabled={!newInvestment.investmentAmount}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                Update Investment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
                onConfirm={confirm.onConfirm || (() => { })}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />
        </div>
    );
};

export default InvestmentManagement;
